import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useNotifier } from "../../../Core/Notifier";
import { useCreateDriverMutation } from "../../../Api/attoDeskApi";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { validateEmail, validatePhoneNumber } from "../../../Components/Validation/validationUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimeSlot { startTime: string; endTime: string; hourlySalary: string; }
interface Availability { day: string; enabled: boolean; timeSlots: TimeSlot[]; }

const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const BLANK_SLOT: TimeSlot = { startTime: "", endTime: "", hourlySalary: "" };

const makeAvailability = (): Availability[] =>
  DAYS.map((day) => ({ day, enabled: false, timeSlots: [{ ...BLANK_SLOT }] }));

// ─── Section card wrapper ─────────────────────────────────────────────────────
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", mb: 2.5, overflow: "hidden" }}>
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#f8fafc" }}>
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.07em" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── Shared field style ───────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13,
    "&:hover fieldset": { borderColor: "#2f3891" },
    "&.Mui-focused fieldset": { borderColor: "#2f3891" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2f3891" },
};

// ─── Component ────────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [imagePreview,  setImagePreview]  = useState<string | null>(null);
  const [isUploading,   setIsUploading]   = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);
  const [availability,  setAvailability]  = useState<Availability[]>(makeAvailability());

  const [createDriver, { isLoading }] = useCreateDriverMutation();

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      driverFirstName: "",
      driverLastName: "",
      driverMobileNum: "",
      driverAltMobileNum: "",
      driverEmail: "",
      driverUserRole: "",
      driverUserName: "",
      driverPassWord: "",
      driverCity: "",
      driverAddress: "",
      driverDescription: "",
    },
    validate: (v) => {
      const e: Record<string, string> = {};
      if (!v.driverFirstName.trim()) e.driverFirstName = "Required";
      if (!v.driverMobileNum.trim()) e.driverMobileNum = "Required";
      if (!v.driverUserRole)         e.driverUserRole  = "Required";
      if (!v.driverUserName.trim())  e.driverUserName  = "Required";
      if (!v.driverPassWord.trim())  e.driverPassWord  = "Required";
      const emailErr  = validateEmail(v.driverEmail);
      const mobileErr = validatePhoneNumber(v.driverMobileNum);
      const altErr    = validatePhoneNumber(v.driverAltMobileNum);
      if (emailErr)  e.driverEmail         = emailErr;
      if (mobileErr) e.driverMobileNum     = mobileErr;
      if (altErr)    e.driverAltMobileNum  = altErr;
      return e;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let driverImg: string | undefined;
        if (selectedImage) {
          setIsUploading(true);
          driverImg = (await uploadImage(selectedImage)) || "";
          setIsUploading(false);
        }
        const payload = {
          ...values,
          driverImg,
          availability: availability.filter((a) => a.enabled),
        };
        const res = await createDriver(payload).unwrap();
        if (!res.status) { showErrorMessage(res.message); }
        else {
          showMessage(res.message);
          navigate("/team/list");
          resetForm();
          setSelectedImage(undefined);
          setImagePreview(null);
          setAvailability(makeAvailability());
        }
      } catch (err: any) { showErrorMessage(err?.data?.message || "Something went wrong"); }
    },
  });

  const canSave = useMemo(() => {
    const v = formik.values;
    return v.driverFirstName.trim() && v.driverMobileNum.trim() &&
           v.driverUserRole && v.driverUserName.trim() && v.driverPassWord.trim();
  }, [formik.values]);

  // ── Image handling ────────────────────────────────────────────────────────
  const resizeImage = (file: File, max: number): Promise<File> =>
    new Promise((res, rej) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const ratio = Math.min(max / img.width, max / img.height, 1);
          const canvas = document.createElement("canvas");
          canvas.width  = img.width  * ratio;
          canvas.height = img.height * ratio;
          canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((b) => {
            if (b) res(new File([b], file.name, { type: file.type }));
            else rej(new Error("Canvas empty"));
          }, file.type);
        };
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg","image/png","image/jpg","image/gif"].includes(file.type)) {
      showErrorMessage("Must be jpeg, png, jpg, or gif"); return;
    }
    if (file.size > 2048 * 1024) { showErrorMessage("Max 2 MB"); return; }
    try {
      const resized = await resizeImage(file, 500);
      setSelectedImage(resized);
      setImagePreview(URL.createObjectURL(resized));
    } catch { showErrorMessage("Failed to process image"); }
  };

  // ── Availability helpers ──────────────────────────────────────────────────
  const toggleDay = (i: number) =>
    setAvailability((prev) => prev.map((a, idx) => idx === i ? { ...a, enabled: !a.enabled } : a));

  const addSlot = (i: number) =>
    setAvailability((prev) => prev.map((a, idx) =>
      idx === i ? { ...a, timeSlots: [...a.timeSlots, { ...BLANK_SLOT }] } : a
    ));

  const removeSlot = (dayIdx: number, slotIdx: number) =>
    setAvailability((prev) => prev.map((a, idx) =>
      idx === dayIdx
        ? { ...a, timeSlots: a.timeSlots.filter((_, si) => si !== slotIdx) }
        : a
    ));

  const updateSlot = (dayIdx: number, slotIdx: number, field: keyof TimeSlot, val: string) =>
    setAvailability((prev) => prev.map((a, idx) =>
      idx === dayIdx
        ? { ...a, timeSlots: a.timeSlots.map((s, si) => si === slotIdx ? { ...s, [field]: val } : s) }
        : a
    ));

  if (isLoading || isUploading)
    return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress size={36} /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 900, mx: "auto" }}>

      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Tooltip title="Back to team list" arrow>
          <IconButton onClick={() => navigate("/team/list")}
            sx={{ border: "1px solid #e2e8f0", borderRadius: 2, width: 36, height: 36, "&:hover": { bgcolor: "#f1f5f9" } }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#475569" }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: "linear-gradient(135deg,#2f3891,#2563eb)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GroupsOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>Add Team Member</Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>Fill in the details below to create a new team member</Typography>
        </Box>
        {/* Top-right save button */}
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <Button onClick={() => { formik.resetForm(); setSelectedImage(undefined); setImagePreview(null); setAvailability(makeAvailability()); }}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, color: "#64748b", border: "1px solid #e2e8f0", px: 2 }}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={() => formik.handleSubmit()}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, bgcolor: canSave ? "#2f3891" : "#e2e8f0", color: canSave ? "#fff" : "#94a3b8", px: 2.5, "&:hover": { bgcolor: canSave ? "#2f3891" : "#e2e8f0" } }}>
            Save Member
          </Button>
        </Box>
      </Box>

      {/* ── Photo upload (compact, top) ─────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", mb: 2.5, p: 2.5, display: "flex", alignItems: "center", gap: 3 }}>
        {/* Preview */}
        <Box sx={{ width: 80, height: 80, borderRadius: "50%", border: "2px dashed #e2e8f0", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
          {imagePreview ? (
            <Box component="img" src={imagePreview} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <CloudUploadIcon sx={{ fontSize: 28, color: "#cbd5e1" }} />
          )}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1e293b", mb: 0.5 }}>Profile Photo</Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1 }}>JPEG, PNG or GIF · Max 2 MB</Typography>
          <Button component="label" size="small" variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, borderColor: "#e2e8f0", color: "#475569", "&:hover": { borderColor: "#2f3891", color: "#2f3891" } }}>
            {imagePreview ? "Change photo" : "Upload photo"}
            <input type="file" accept="image/*" hidden onChange={handleImageChange} />
          </Button>
          {imagePreview && (
            <Button size="small" onClick={() => { setSelectedImage(undefined); setImagePreview(null); }}
              sx={{ ml: 1, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#e11d48" }}>
              Remove
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Basic Information ───────────────────────────────────────────── */}
      <Section title="Basic Information">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField size="small" label="First Name *" name="driverFirstName"
            value={formik.values.driverFirstName} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={!!(formik.touched.driverFirstName && formik.errors.driverFirstName)}
            helperText={formik.touched.driverFirstName && formik.errors.driverFirstName} sx={fieldSx} />
          <TextField size="small" label="Last Name" name="driverLastName"
            value={formik.values.driverLastName} onChange={formik.handleChange} sx={fieldSx} />
          <TextField size="small" label="Mobile Number *" name="driverMobileNum"
            value={formik.values.driverMobileNum} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={!!(formik.touched.driverMobileNum && formik.errors.driverMobileNum)}
            helperText={formik.touched.driverMobileNum && formik.errors.driverMobileNum} sx={fieldSx} />
          <TextField size="small" label="Alt Mobile Number" name="driverAltMobileNum"
            value={formik.values.driverAltMobileNum} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={!!(formik.touched.driverAltMobileNum && formik.errors.driverAltMobileNum)}
            helperText={formik.touched.driverAltMobileNum && formik.errors.driverAltMobileNum} sx={fieldSx} />
          <TextField size="small" label="Email Address" name="driverEmail"
            value={formik.values.driverEmail} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={!!(formik.touched.driverEmail && formik.errors.driverEmail)}
            helperText={formik.touched.driverEmail && formik.errors.driverEmail} sx={fieldSx} />
          <TextField size="small" label="Role *" name="driverUserRole" select
            value={formik.values.driverUserRole} onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={!!(formik.touched.driverUserRole && formik.errors.driverUserRole)}
            helperText={formik.touched.driverUserRole && formik.errors.driverUserRole} sx={fieldSx}>
            <MenuItem value="driver">Driver</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </TextField>
        </Box>
      </Section>

      {/* ── Account Details ─────────────────────────────────────────────── */}
      <Section title="Account Details">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField size="small" label="Username *" name="driverUserName"
            value={formik.values.driverUserName} onChange={formik.handleChange} onBlur={formik.handleBlur}
            autoComplete="off"
            inputProps={{ autoComplete: "off", "data-lpignore": "true", "data-form-type": "other" }}
            error={!!(formik.touched.driverUserName && formik.errors.driverUserName)}
            helperText={formik.touched.driverUserName && formik.errors.driverUserName} sx={fieldSx} />
          <TextField size="small" label="Password *" name="driverPassWord"
            type={showPassword ? "text" : "password"}
            value={formik.values.driverPassWord} onChange={formik.handleChange} onBlur={formik.handleBlur}
            autoComplete="new-password"
            inputProps={{ autoComplete: "new-password", "data-lpignore": "true", "data-form-type": "other" }}
            error={!!(formik.touched.driverPassWord && formik.errors.driverPassWord)}
            helperText={formik.touched.driverPassWord && formik.errors.driverPassWord}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword((p) => !p)}>
                    {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx} />
        </Box>
      </Section>

      {/* ── Address & Notes ─────────────────────────────────────────────── */}
      <Section title="Address & Notes">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField size="small" label="City" name="driverCity"
            value={formik.values.driverCity} onChange={formik.handleChange} sx={fieldSx} />
          <TextField size="small" label="Address" name="driverAddress"
            value={formik.values.driverAddress} onChange={formik.handleChange} sx={fieldSx} />
          <TextField size="small" label="Notes / Description" name="driverDescription" multiline rows={2}
            value={formik.values.driverDescription} onChange={formik.handleChange}
            sx={{ ...fieldSx, gridColumn: "1 / -1" }} />
        </Box>
      </Section>

      {/* ── Availability Schedule ────────────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", mb: 2.5, overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.07em" }}>
            Availability Schedule
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Toggle each day and add time slots</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {availability.map((avail, dayIdx) => (
            <Box key={avail.day} sx={{ mb: dayIdx < availability.length - 1 ? 1 : 0 }}>
              {/* Day row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, px: 1, borderRadius: 2, bgcolor: avail.enabled ? "#f0f4ff" : "#fafafa", border: `1px solid ${avail.enabled ? "#c7d2fe" : "#f1f5f9"}`, mb: avail.enabled ? 1 : 0 }}>
                <Switch
                  checked={avail.enabled}
                  onChange={() => toggleDay(dayIdx)}
                  size="small"
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#2f3891" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#2f3891" } }}
                />
                <Typography sx={{ fontWeight: 600, fontSize: 13, color: avail.enabled ? "#3730a3" : "#94a3b8", minWidth: 90 }}>
                  {avail.day}
                </Typography>
                {!avail.enabled && (
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Not available</Typography>
                )}
                {avail.enabled && (
                  <Box sx={{ ml: "auto" }}>
                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                      onClick={() => addSlot(dayIdx)}
                      sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, color: "#2f3891", px: 1, py: 0.25, "&:hover": { bgcolor: "#eef2ff" } }}>
                      Add slot
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Time slots */}
              {avail.enabled && avail.timeSlots.map((slot, slotIdx) => (
                <Box key={slotIdx} sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 1, mb: 0.75, pl: 2 }}>
                  <TextField size="small" label="Start Time" type="time" value={slot.startTime}
                    onChange={(e) => updateSlot(dayIdx, slotIdx, "startTime", e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                  <TextField size="small" label="End Time" type="time" value={slot.endTime}
                    onChange={(e) => updateSlot(dayIdx, slotIdx, "endTime", e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={fieldSx} />
                  <TextField size="small" label="Hourly Rate ($)" value={slot.hourlySalary}
                    onChange={(e) => updateSlot(dayIdx, slotIdx, "hourlySalary", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: 12, color: "#94a3b8" }}>$</Typography></InputAdornment> }}
                    sx={fieldSx} />
                  <Tooltip title="Remove slot" arrow>
                    <span>
                      <IconButton size="small" disabled={avail.timeSlots.length === 1}
                        onClick={() => removeSlot(dayIdx, slotIdx)}
                        sx={{ color: "#e11d48", "&:disabled": { color: "#e2e8f0" } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}

              {dayIdx < availability.length - 1 && !avail.enabled && (
                <Divider sx={{ my: 0.25, borderColor: "#f1f5f9" }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom save bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 1 }}>
        <Button onClick={() => navigate("/team/list")}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, color: "#64748b", border: "1px solid #e2e8f0", px: 2.5 }}>
          Cancel
        </Button>
        <Button disabled={!canSave} onClick={() => formik.handleSubmit()}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3, bgcolor: canSave ? "#2f3891" : "#e2e8f0", color: canSave ? "#fff" : "#94a3b8", "&:hover": { bgcolor: canSave ? "#2f3891" : "#e2e8f0" } }}>
          Save Team Member
        </Button>
      </Box>
    </Box>
  );
};

export default Index;
